import { TrophyIcon } from "@heroicons/react/24/outline";
import {
    activityCategories,
    activityCategoryLabels,
    CrmMode,
    MarketingGoalTargets,
    OutcomeGoalMetric,
    outcomeGoalMetricLabels,
    outcomeGoalMetrics,
    outcomeGoalMetricsForCrmMode,
    unknownActivityTypeLabel,
} from "@sp-crm/core";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import {
    ActivityTypeOption,
    byActivityTypeName,
} from "components/tasks/activity-type-options";
import { GoalColumnIcon, outcomeGoalMetricIcons } from "./goal-column-icons";
import { goalActivityTypeOptions } from "./goal-columns";

export type TargetRow =
    | { kind: "activity"; key: string; label: string; icon: GoalColumnIcon }
    | { kind: "outcome"; key: OutcomeGoalMetric; label: string; icon: GoalColumnIcon };

export interface TargetRowSection {
    title: string;
    icon: GoalColumnIcon;
    rows: TargetRow[];
}

export const unknownActivityRow = (key: string): TargetRow => ({
    kind: "activity",
    key,
    label: unknownActivityTypeLabel,
    icon: activityCategoryIcons.other,
});

export const flattenTargets = (
    targets: MarketingGoalTargets,
): Record<string, number> => ({
    ...targets.activity,
    ...Object.fromEntries(outcomeGoalMetrics.map(metric => [metric, targets[metric]])),
});

export const editorOutcomeMetrics = (
    mode: CrmMode,
    baselines: MarketingGoalTargets[],
): OutcomeGoalMetric[] => {
    const offered = new Set<OutcomeGoalMetric>(outcomeGoalMetricsForCrmMode(mode));
    return outcomeGoalMetrics.filter(
        metric => offered.has(metric) || baselines.some(baseline => baseline[metric] > 0),
    );
};

const baselineActivityKeys = (baselines: MarketingGoalTargets[]): string[] => {
    const keys = new Set<string>();
    for (const baseline of baselines) {
        for (const key of Object.keys(baseline.activity)) {
            keys.add(key);
        }
    }
    return Array.from(keys);
};

export const buildTargetRowSections = (
    activityTypeOptions: ActivityTypeOption[],
    crmMode: CrmMode,
    baselines: MarketingGoalTargets[],
): TargetRowSection[] => {
    const referencedKeys = baselineActivityKeys(baselines);
    const availableOptions = goalActivityTypeOptions(
        activityTypeOptions,
        new Set(referencedKeys),
    );
    const knownIds = new Set<string>(availableOptions.map(option => option.id));
    const activitySections = activityCategories
        .map(category => {
            const rows = availableOptions
                .filter(
                    option =>
                        option.category === category &&
                        (!option.isArchived || referencedKeys.includes(option.id)),
                )
                .sort(byActivityTypeName)
                .map<TargetRow>(option => ({
                    kind: "activity",
                    key: option.id,
                    label: option.name,
                    icon: activityCategoryIcons[category],
                }));
            if (category === "other") {
                for (const key of referencedKeys) {
                    if (!knownIds.has(key)) {
                        rows.push(unknownActivityRow(key));
                    }
                }
            }
            return {
                title: activityCategoryLabels[category],
                icon: activityCategoryIcons[category],
                rows,
            };
        })
        .filter(section => section.rows.length > 0);
    const outcomeSection: TargetRowSection = {
        title: "Outcome",
        icon: TrophyIcon,
        rows: editorOutcomeMetrics(crmMode, baselines).map<TargetRow>(metric => ({
            kind: "outcome",
            key: metric,
            label: outcomeGoalMetricLabels[metric],
            icon: outcomeGoalMetricIcons[metric],
        })),
    };
    return [...activitySections, outcomeSection];
};
